import type { BtlApplication } from '@api/btlApplicationForm/types';
import type { KeyboardEvent } from 'react';
import { resolveNavItemColorClass, resolveNavItemContainerClass } from './caseSidebarNavItemStyles';
import { getCaseSidebarItemIcon } from '@components/CaseSidebar/config/items';
import { CaseSidebarApplicantsSubtitle } from './CaseSidebarApplicantsSubtitle';
import { CaseSidebarItemSubtitle } from './CaseSidebarItemSubtitle';
import { CaseSidebarItemWarnings } from './CaseSidebarItemWarnings';
import type { CaseSectionItem } from '../types';
import { CaseSidebarItemId } from '../types';

interface CaseSidebarNavItemProps {
  item: CaseSectionItem;
  isActive: boolean;
  application?: BtlApplication;
  onClick?: () => void;
}

export function CaseSidebarNavItem({ item, isActive, application, onClick }: CaseSidebarNavItemProps) {
  const hasWarnings = item.warnings.length > 0;
  const isClickable = !item.isDisabled && onClick != null;
  const styleParams = { hasWarnings, isActive, isDisabled: item.isDisabled, isClickable };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isClickable) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      role='button'
      tabIndex={isClickable ? 0 : -1}
      aria-disabled={item.isDisabled}
      data-testid={`case-sidebar-item-${item.id}`}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      className={resolveNavItemContainerClass(styleParams)}
    >
      <span
        className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center text-[17px] ${resolveNavItemColorClass({
          ...styleParams,
          variant: 'icon',
        })}`}
      >
        {getCaseSidebarItemIcon(item.iconItemId ?? item.id)}
      </span>
      <span className='min-w-0 flex-1'>
        <span
          className={`block text-sm font-normal leading-5 ${resolveNavItemColorClass({
            ...styleParams,
            variant: 'title',
          })}`}
        >
          {item.title}
        </span>
        {!item.isDisabled &&
          (item.id === CaseSidebarItemId.APPLICANTS && application ? (
            <CaseSidebarApplicantsSubtitle application={application} />
          ) : (
            <CaseSidebarItemSubtitle item={item} hasWarnings={hasWarnings} isActive={isActive} />
          ))}
      </span>
      {hasWarnings && <CaseSidebarItemWarnings warnings={item.warnings} />}
    </div>
  );
}
